import { useState, useEffect } from "react";
import { useAuth } from "@/hooks/useAuth";
import { DashboardLayout } from "@/components/layouts/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { api } from "@/lib/axios";
import { toast } from "sonner";
import {
  DollarSign,
  TrendingUp,
  Users,
  Home,
  Eye,
  Heart,
  MessageSquare,
  Building2,
  Clock,
  CheckCircle,
  CreditCard,
  Activity,
} from "lucide-react";
import { formatDistanceToNow } from 'date-fns';
import { es } from 'date-fns/locale';

interface Property {
  _id: string;
  status: 'active' | 'rented' | 'sold' | 'inactive';
  views: number;
  inquiries: number;
  favorites: number;
  title: string;
  images: string[];
}

interface DashboardData {
  properties: Property[];
  favorites: any[];
  unreadMessages: number;
  performanceMetrics: {
    totalViews: number;
    totalInquiries: number;
    totalFavorites: number;
  };
  income: {
    totalIncome: number;
    pendingPayments: number;
  };
  propertyStatus: {
    active: number;
    rented: number;
    sold: number;
    inactive: number;
  };
}

export function DashboardPage() {
  const { user } = useAuth();
  const [dashboardData, setDashboardData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      // Obtener propiedades del usuario
      const userPropertiesResponse = await api.get('/api/properties/user/properties');
      const properties = userPropertiesResponse.data;

      // Obtener favoritos
      const favoritesResponse = await api.get('/api/favorites');
      const favorites = favoritesResponse.data;

      // Obtener cantidad de mensajes no leídos
      const unreadMessagesResponse = await api.get('/api/messages/unread/count');
      const unreadMessages = unreadMessagesResponse.data.count;

      // Calcular métricas de rendimiento
      const totalViews = properties.reduce((sum: number, prop: Property) => sum + (prop.views || 0), 0);
      const totalInquiries = properties.reduce((sum: number, prop: Property) => sum + (prop.inquiries || 0), 0);
      const totalFavorites = properties.reduce((sum: number, prop: Property) => sum + (prop.favorites || 0), 0);

      // Calcular estado de las propiedades
      const propertyStatus = {
        active: properties.filter((p: Property) => p.status === 'active').length,
        rented: properties.filter((p: Property) => p.status === 'rented').length,
        sold: properties.filter((p: Property) => p.status === 'sold').length,
        inactive: properties.filter((p: Property) => p.status === 'inactive').length,
      };

      setDashboardData({
        properties,
        favorites,
        unreadMessages,
        performanceMetrics: {
          totalViews,
          totalInquiries,
          totalFavorites,
        },
        income: {
          totalIncome: 0,
          pendingPayments: 0,
        },
        propertyStatus,
      });

    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      toast.error('Error al cargar los datos del dashboard');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const stats = [
    {
      title: "Propiedades Listadas",
      value: dashboardData?.properties?.length || 0,
      icon: Building2,
      description: "Total de propiedades publicadas",
      trend: `${dashboardData?.properties?.length ? '+' : ''}${dashboardData?.properties?.length || 0} activas`
    },
    {
      title: "Propiedades Favoritas",
      value: dashboardData?.favorites?.length || 0,
      icon: Heart,
      description: "Propiedades guardadas",
      trend: `${dashboardData?.favorites?.length || 0} total`
    },
    {
      title: "Mensajes Nuevos",
      value: dashboardData?.unreadMessages || 0,
      icon: MessageSquare,
      description: "Mensajes sin leer",
      trend: `${dashboardData?.unreadMessages || 0} pendientes`
    }
  ];

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-full">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div>
          <h2 className="text-3xl font-bold tracking-tight">Dashboard</h2>
          <p className="text-muted-foreground">
            Bienvenido de nuevo, {user?.name}
          </p>
        </div>

        {/* Stats Grid */}
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {stats.map((stat, index) => (
            <Card key={index}>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">
                  {stat.title}
                </CardTitle>
                <stat.icon className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{stat.value}</div>
                <p className="text-xs text-muted-foreground">
                  {stat.trend}
                </p>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Performance Metrics */}
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          <Card>
            <CardHeader>
              <CardTitle>Rendimiento</CardTitle>
              <CardDescription>
                Métricas de rendimiento de tus propiedades
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <Eye className="mr-2 h-4 w-4 text-muted-foreground" />
                    <span className="text-sm font-medium">Vistas Totales</span>
                  </div>
                  <span className="font-bold">{dashboardData?.performanceMetrics.totalViews || 0}</span>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <Heart className="mr-2 h-4 w-4 text-muted-foreground" />
                    <span className="text-sm font-medium">Favoritos</span>
                  </div>
                  <span className="font-bold">{dashboardData?.performanceMetrics.totalFavorites || 0}</span>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <MessageSquare className="mr-2 h-4 w-4 text-muted-foreground" />
                    <span className="text-sm font-medium">Consultas</span>
                  </div>
                  <span className="font-bold">{dashboardData?.performanceMetrics.totalInquiries || 0}</span>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Ingresos</CardTitle>
              <CardDescription>
                Resumen de ingresos y pagos pendientes
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <DollarSign className="mr-2 h-4 w-4 text-muted-foreground" />
                    <span className="text-sm font-medium">Ingresos Totales</span>
                  </div>
                  <span className="font-bold">${dashboardData?.income.totalIncome.toLocaleString() || '0'}</span>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <CreditCard className="mr-2 h-4 w-4 text-muted-foreground" />
                    <span className="text-sm font-medium">Pagos Pendientes</span>
                  </div>
                  <span className="font-bold">${dashboardData?.income.pendingPayments.toLocaleString() || '0'}</span>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Estado de Propiedades</CardTitle>
              <CardDescription>
                Distribución de tus propiedades por estado
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <Activity className="mr-2 h-4 w-4 text-muted-foreground" />
                    <span className="text-sm font-medium">Activas</span>
                  </div>
                  <span className="font-bold text-green-500">{dashboardData?.propertyStatus.active || 0}</span>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <Home className="mr-2 h-4 w-4 text-muted-foreground" />
                    <span className="text-sm font-medium">Alquiladas</span>
                  </div>
                  <span className="font-bold text-blue-500">{dashboardData?.propertyStatus.rented || 0}</span>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <Users className="mr-2 h-4 w-4 text-muted-foreground" />
                    <span className="text-sm font-medium">Vendidas</span>
                  </div>
                  <span className="font-bold text-yellow-500">{dashboardData?.propertyStatus.sold || 0}</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </DashboardLayout>
  );
} 